import React, { FC, useCallback, useMemo, useState } from 'react';
import PaginatedReduxTableWithHeader from '../../common/PaginatedReduxTableWithHeader';
import { connect } from 'react-redux';
import { withRouter } from 'react-router';
import { Button } from 'antd';
import { EmptyState } from '../../empty_state/EmptyState';
import {
	troubleshootingGuidesRestCrudThunksForBuyer,
	troubleshootingGuidesRestCrudThunksForSupplier,
} from '../../../thunks/troubleshooting_guides_thunks';
import TSLocationsDisplay from './TSLocationsDisplay';
import TSProblemTypesDisplay from './TSProblemTypesDisplay';
import TSPrioritiesDisplay from './TSPrioritiesDisplay';
import { TROUBLESHOOTING_GUIDES_CRUD_ACTION_CREATORS } from '../../../actions/troubleshooting_guides_actions';
import { nullSafeGetOrElse } from '../../../utils/DataAccessUtils';
import { getLinkWIthBackLinkParams } from '../../../utils/HistoryUtils';
import { ROLE_TYPES } from '../../../utils/DataConstants';
import { withUserType } from '../../withUserType';
import TSSorters from './TSSorters';
import WorkOrderCreateButton from '../../common/WorkOrderCreateButton';
import TroubleshootingGuideNewOrEditModal from '../TroubleshootingGuideNewOrEditModal';
import AccessPermissionChecker from '../../common/AccessPermissionChecker';
import { PERMISSION_NAMES } from '../../../utils/AuthUtils';
import { useFilters } from '../../../hooks/useFilters';
import { getTroubleshootingFilterConfig } from '../../../utils/TroubleshootingFilterUtils';

const TC_NAME = 'troubleshootingGuidesIndex';
const TS_ADVANCED_FILTERS = 'troubleshooting_advanced_filters';

const TroubleshootingOverviewAllPage: FC<any> = ({
	updateFilters,
	troubleshootingGuides,
	clearAndUpdateFilters,
	history,
	location,
	updateSorters,
	userType,
}): React.ReactElement => {
	const [newGuideVisible, setNewGuideVisible] = useState(false);

	const updateTSFilters = useCallback(
		(filters) => updateFilters(filters, TC_NAME),
		[updateFilters]
	);

	const allFilters = useFilters({
		filterTargetCollectionName: TS_ADVANCED_FILTERS,
		overviewCollectionName: TC_NAME,
		updateFilters: updateTSFilters,
	});

	const filterConfig = useMemo(
		() => getTroubleshootingFilterConfig({ filters: allFilters }),
		[allFilters]
	);

	const onRow = useCallback(
		(record) => ({
			onClick: () =>
				history.push(
					getLinkWIthBackLinkParams(
						location,
						'Back to guides',
						`/${userType}/workflows/troubleshooting/detail/${record.id}`
					)
				),
		}),
		[history, location, userType]
	);

	const onNewGuideSuccess = useCallback(
		(guide) => {
			history.push(`/${userType}/workflows/troubleshooting/detail/${guide.id}`);
		},
		[history, userType]
	);

	const _hideNewGuide = () => setNewGuideVisible(false);
	const _showNewGuide = () => setNewGuideVisible(true);

	const createNewButton = (
		<AccessPermissionChecker name={PERMISSION_NAMES.ADD_TROUBLESHSHOOTING}>
			<Button style={{ marginLeft: 16 }} type="primary" size="large" onClick={_showNewGuide}>
				New Guide
			</Button>
		</AccessPermissionChecker>
	);

	const isAssociationsNotSpecified = (record) =>
		nullSafeGetOrElse('problemTypeIds', record, []).length === 0 &&
		nullSafeGetOrElse('locationIds', record, []).length === 0 &&
		nullSafeGetOrElse('workOrderPriorityIds', record, []).length === 0;

	const columns = useMemo(
		() => [
			{
				title: 'Name',
				dataIndex: 'name',
				render: (name, record) => {
					const stepCount = nullSafeGetOrElse('troubleshootingSteps', record, []).length;
					return (
						<div className="flex flex-col">
							<div>{name || '--'}</div>
							{record.description ? (
								<div className="text-gray-400">{record.description}</div>
							) : null}
							<div className="text-gray-400">{`${stepCount} step${
								stepCount !== 1 ? 's' : ''
							}`}</div>
						</div>
					);
				},
			},
			{
				title: 'Problem Types',
				dataIndex: 'problemTypes',
				render: (problemTypes, record) => (
					<TSProblemTypesDisplay
						problemTypes={problemTypes}
						notSpecified={isAssociationsNotSpecified(record)}
					/>
				),
			},
			{
				title: 'Locations',
				dataIndex: 'locations',
				render: (locations, record) => (
					<TSLocationsDisplay
						locations={locations}
						notSpecified={isAssociationsNotSpecified(record)}
					/>
				),
			},
			{
				title: 'Priorities',
				dataIndex: 'workOrderPriorities',
				render: (workOrderPriorities, record) => (
					<TSPrioritiesDisplay
						priorities={workOrderPriorities}
						notSpecified={isAssociationsNotSpecified(record)}
					/>
				),
			},
		],
		[]
	);

	return (
		<>
			<PaginatedReduxTableWithHeader
				targetCollectionName={TC_NAME}
				updateFilters={updateFilters}
				stateSlice={troubleshootingGuides}
				clearAndUpdateFilters={clearAndUpdateFilters}
				filterConfig={filterConfig}
				sorters={TSSorters}
				updateSorters={updateSorters}
				entityCollectionName="troubleshooting_guides"
				tableColumns={columns}
				onTableRow={onRow}
				fetchData={
					userType === ROLE_TYPES.SUPPLIER
						? troubleshootingGuidesRestCrudThunksForSupplier.read
						: troubleshootingGuidesRestCrudThunksForBuyer.read
				}
				rightActions={
					<div className="flex flex-row items-center gap-2">
						<WorkOrderCreateButton
							align="center"
							size="large"
							type="ghost"
							backText={'Back to troubleshooting guides'}
							title="Preview"
							getLink={(location, BACK_TEXT, path) => {
								const params = {
									onlyForPreview: true,
								};
								const paramsString = new URLSearchParams(params).toString();

								return getLinkWIthBackLinkParams(location, BACK_TEXT, `${path}?${paramsString}`);
							}}
						/>
						{createNewButton}
					</div>
				}
				showHeader
				emptyState={
					<EmptyState
						graphic={
							<img
								alt="No Data"
								style={{ marginBottom: 8 }}
								src="https://s3.amazonaws.com/mock-data-assets/categories/images/box.svg"
							/>
						}
						headline={'No troubleshooting found'}
						body={<div style={{ textAlign: 'center' }}>{createNewButton}</div>}
					/>
				}
			/>
			{newGuideVisible ? (
				<TroubleshootingGuideNewOrEditModal
					onSuccess={onNewGuideSuccess}
					onCancel={_hideNewGuide}
				/>
			) : null}
		</>
	);
};

const mapStateToProps = (state) => ({
	troubleshootingGuides: state.troubleshooting_guides,
});

const mapDispatchToProps = (dispatch) => ({
	updateFilters: (filters, targetCollection) =>
		dispatch(TROUBLESHOOTING_GUIDES_CRUD_ACTION_CREATORS.updateFilters(filters, targetCollection)),
	updateSorters: (sortBy, order, targetCollection) =>
		dispatch(
			TROUBLESHOOTING_GUIDES_CRUD_ACTION_CREATORS.updateSorting(sortBy, order, targetCollection)
		),
	clearAndUpdateFilters: (filters, targetCollectionName) =>
		dispatch(
			TROUBLESHOOTING_GUIDES_CRUD_ACTION_CREATORS.clearAndUpdateFilters(
				filters,
				targetCollectionName
			)
		),
});

export default withRouter<any, any>(
	withUserType(connect(mapStateToProps, mapDispatchToProps)(TroubleshootingOverviewAllPage))
);
